<template>
	<span
		v-tooltip="dueDateTooltip"
		class="task-due-date"
		:class="classAddons"
		:aria-label="
			dueDateData[1] ? $t(`task_deadline_${dueDateData[1]}`) : undefined
		"
	>
		{{ dueDateData[0] }}
		<template v-if="dueDateTime && showTime">
			<br />
			<span class="task-due-date__time">
				{{ formatTime(adjustedDueDateTime) }}
			</span>
		</template>
	</span>
</template>

<script setup lang="ts">
import { useFormatter, useTranslation } from '@/composables';
import { type Task } from '@/generated/graphql';
import {
	addDays,
	isPast,
	isToday,
	isTomorrow,
	isWithinInterval,
} from 'date-fns';
import { computed } from 'vue';
import { resetSapiDateString } from '@/utils/formatting';

export type TaskDueDateProps = {
	dueDateTime: Task['dueDateTime'];
	isDone?: Task['isDone'];
	showTime?: boolean;
};

const props = defineProps<TaskDueDateProps>();

const { t } = useTranslation();
const {
	formatDateNoCurrentYear,
	formatDate,
	formatDateShortNoCurrentYear,
	formatTime,
} = useFormatter();

const adjustedDueDateTime = computed(() => {
	return props.dueDateTime ? resetSapiDateString(props.dueDateTime) : '';
});

const dueDateTooltip = computed(() => {
	return props.dueDateTime ? formatDate(adjustedDueDateTime.value) : '';
});

type DueDateSeverity = undefined | 'warning' | 'critical';
type DueDateLabel = string;

const dueDateData = computed<[DueDateLabel, DueDateSeverity]>(() => {
	if (!props.dueDateTime) {
		return ['', undefined];
	}

	if (isToday(adjustedDueDateTime.value)) {
		return [t('today'), 'warning'];
	} else if (isTomorrow(adjustedDueDateTime.value)) {
		return [t('tomorrow'), 'warning'];
	} else if (isPast(adjustedDueDateTime.value) && !props.isDone) {
		return [formatDateNoCurrentYear(adjustedDueDateTime.value), 'critical'];
	} else if (
		isWithinInterval(adjustedDueDateTime.value, {
			start: Date.now(),
			end: addDays(Date.now(), 7),
		})
	) {
		return [
			formatDateShortNoCurrentYear(adjustedDueDateTime.value),
			'warning',
		];
	}

	return [formatDateNoCurrentYear(adjustedDueDateTime.value), undefined];
});

const classAddons = computed(() => {
	return { [`task-due-date--${dueDateData.value[1]}`]: dueDateData.value[1] };
});
</script>

<style lang="scss">
.task-due-date {
	font-feature-settings: 'tnum';

	&__time {
		font-size: 1.25rem;
		color: $neutral-70;
	}

	&--warning {
		color: $indigo-50;
	}

	&--critical {
		color: $red-50;
	}
}
</style>
